import { countGoalsByLifeArea, sortItems } from '@griply/utils';
import { useMemo } from 'react';
import { useDataGridSorting } from 'shared/hooks/use-data-grid-sorting';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';

import { mapLifeAreaToRowData } from './map-life-area-to-row-data';
import { sortLifeAreaRows } from './sort-life-area-rows';
import { LifeAreaColumnType } from './types';

const emptySorting: ID[] = [];

export const useLifeAreaOverviewRows = (
  lifeAreas: LifeArea[],
  goals: Goal[],
  lifeAreaSorting: ID[] = emptySorting,
) => {
  const {
    sortMode,
    type: sortColumn,
    onSortChange,
  } = useDataGridSorting<LifeAreaColumnType>();

  const countsByLifeArea = useMemo(() => countGoalsByLifeArea(goals), [goals]);

  const rowsByState = useMemo(() => {
    const toRow = (lifeArea: LifeArea) =>
      mapLifeAreaToRowData(lifeArea, countsByLifeArea);

    return {
      active: sortItems(
        lifeAreas.filter(
          ({ frozenAt, archivedAt }) => !frozenAt && !archivedAt,
        ),
        lifeAreaSorting,
        'id',
      ).map(toRow),
      frozen: lifeAreas.filter(({ frozenAt }) => !!frozenAt).map(toRow),
      archived: sortItems(
        lifeAreas.filter(({ archivedAt }) => !!archivedAt),
        lifeAreaSorting,
        'id',
      ).map(toRow),
    };
  }, [lifeAreas, lifeAreaSorting, countsByLifeArea]);

  const activeRows = useMemo(
    () => sortLifeAreaRows(rowsByState.active, sortColumn, sortMode),
    [rowsByState.active, sortColumn, sortMode],
  );

  return {
    activeRows,
    frozenRows: rowsByState.frozen,
    archivedRows: rowsByState.archived,
    sortMode,
    sortColumn,
    onSortChange,
  };
};
